import React, { useContext, useMemo, useState } from 'react';

import { Box, Fade, Paper, Typography } from '@material-ui/core';
import { OnboardingStepTitle } from '@vyce/core/src/components';
import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { Team } from '@vyce/core/src/types';

interface Props {
  teams: Team[];
  selectedTeams?: Team[];
  setSelectedTeams: (teams: Team[]) => void;
  /** Render only the checkbox list, without the Paper card and title. */
  embedded?: boolean;
}

const getTeamKey = (team: Team) => team.uuid ?? team.name;

export const FormTeamsStep: React.FC<Props> = ({ teams, selectedTeams, setSelectedTeams, embedded }) => {
  const { isMobile } = useContext(DeviceContext);
  const [internalSelected, setInternalSelected] = useState<Team[]>([]);
  const selected = selectedTeams ?? internalSelected;

  const sortedTeams = useMemo(() => {
    return [...(teams || [])].sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));
  }, [teams]);

  const selectedKeys = useMemo(() => new Set(selected.map(getTeamKey)), [selected]);

  const updateSelected = (next: Team[]) => {
    if (!selectedTeams) {
      setInternalSelected(next);
    }
    setSelectedTeams(next);
  };

  const toggleTeam = (team: Team) => {
    const key = getTeamKey(team);
    const next = selectedKeys.has(key)
      ? selected.filter(item => getTeamKey(item) !== key)
      : [...selected, team];
    updateSelected(next);
  };

  const list = (
    <Box display="flex" flexDirection="column" marginTop={embedded ? 0 : 2} gridGap={12}>
      {!sortedTeams.length ? (
        <Typography color="textSecondary">No Teams found.</Typography>
      ) : (
        sortedTeams.map(team => {
          const checked = selectedKeys.has(getTeamKey(team));
          return (
            <Box
              key={getTeamKey(team)}
              display="flex"
              alignItems={isMobile ? 'flex-start' : 'center'}
              gridGap={8}
            >
              <AppCheckbox
                checked={checked}
                onChange={() => toggleTeam(team)}
                variant="rectangle"
                color="primary"
              />
              <Box display="flex" flexDirection="column">
                <Typography style={{ fontSize: 15, lineHeight: '24px' }}>{team.name}</Typography>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );

  if (embedded) {
    return list;
  }

  return (
    <Fade in={true} timeout={200}>
      <div>
        <Paper variant="outlined" style={{ padding: 24 }}>
          <OnboardingStepTitle text="Which Teams?" />
          {list}
        </Paper>
      </div>
    </Fade>
  );
};
